import React, { useContext, useEffect, useState, useMemo } from "react";
import {
  Box,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Modal,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
  IconButton,
  Button,
  alpha,
  Divider,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Form, FormikProvider, useFormik } from "formik";
import * as Yup from "yup";
import axios from "../../util/axios";
import { Loader } from "../../util/Loader";
import { socketTypeList } from "../../util/Display";
import Constant from "../../util/constants";
import { Context } from "../../context";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: '95%', sm: '550px' },
  bgcolor: "background.paper",
  borderRadius: 2,
  boxShadow: (theme) => `0 24px 48px 0 ${alpha(theme.palette.common.black, 0.4)}`,
  display: 'flex',
  flexDirection: 'column',
  maxHeight: '90vh',
  overflow: 'hidden'
};

const AddVehicleModal = ({ open, onClose, selectedVehicle }) => {
  const { user, message } = useContext(Context);
  const { userDetail } = user;
  const { setMessageOption } = message;
  const isEdit = Boolean(selectedVehicle?._id);

  const [loading, setLoading] = useState(false);

  const validationSchema = Yup.object().shape({
    make: Yup.string().required("Make is required"),
    model: Yup.string().required("Model is required"),
    charger: Yup.string().required("Charger type is required"),
    registrationNo: Yup.string().required("Registration number is required"),
    vinNo: Yup.string(),
    color: Yup.string(),
  });

  const formik = useFormik({
    initialValues: {
      make: selectedVehicle?.make || "",
      model: selectedVehicle?.model || "",
      charger: selectedVehicle?.charger || "",
      registrationNo: selectedVehicle?.registrationNo || "",
      vinNo: selectedVehicle?.vinNo || "",
      color: selectedVehicle?.color || "",
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const payload = {
          ...values,
          user: userDetail._id,
          ...(isEdit && { _id: selectedVehicle._id })
        };

        const endpoint = isEdit ? "user/update-vehicle" : "user/add-vehicle";
        const method = isEdit ? "patch" : "post";

        const response = await axios(`${process.env.REACT_APP_API_END_POINT}${endpoint}`, {
          method,
          data: payload
        });

        if (response.data.message === "Success" || response.status === 200) {
          setMessageOption({
            open: true,
            message: `Vehicle ${isEdit ? "updated" : "added"} successfully!`,
            variant: "success",
          });
          onClose();
        } else {
          throw new Error(response.data.message || "Failed to save vehicle");
        }
      } catch (error) {
        console.error("Submission error:", error);
        setMessageOption({
          open: true,
          message: error.response?.data?.message || "Something went wrong",
          variant: "error",
        });
      } finally {
        setSubmitting(false);
      }
    },
  });

  const { errors, touched, values, handleSubmit, isSubmitting, getFieldProps } = formik;

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={modalStyle}>
        {/* Header */}
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}>
          <Typography variant="h6" fontWeight={700}>
            {isEdit ? "Edit Vehicle" : "Add New Vehicle"}
          </Typography>
          <Tooltip title="Close" placement="bottom">
          <IconButton onClick={onClose} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
          </Tooltip>
        </Box>

        {loading ? (
          <Box sx={{ p: 5, textAlign: 'center' }}>
            <Loader />
          </Box>
        ) : (
          <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 3 }}>
            <FormikProvider value={formik}>
              <Form noValidate onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Make"
                      {...getFieldProps("make")}
                      error={Boolean(touched.make && errors.make)}
                      helperText={touched.make && errors.make}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Model"
                      {...getFieldProps("model")}
                      error={Boolean(touched.model && errors.model)}
                      helperText={touched.model && errors.model}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControl fullWidth size="small" error={Boolean(touched.charger && errors.charger)}>
                      <InputLabel>Charger Type</InputLabel>
                      <Select
                        label="Charger Type"
                        {...getFieldProps("charger")}
                      >
                        {socketTypeList.map((socket) => (
                          <MenuItem key={socket.value || socket.type} value={socket.value || socket.type}>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Box sx={{ width: 24, height: 24, '& svg': { width: '100%', height: '100%' } }}>
                                {socket.src}
                              </Box>
                              <Typography variant="body2">{Constant.socketType[socket.value || socket.type] || socket.type}</Typography>
                            </Stack>
                          </MenuItem>
                        ))}
                      </Select>
                      {touched.charger && <FormHelperText>{errors.charger}</FormHelperText>}
                    </FormControl>
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Registration Number"
                      {...getFieldProps("registrationNo")}
                      error={Boolean(touched.registrationNo && errors.registrationNo)}
                      helperText={touched.registrationNo && errors.registrationNo}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="VIN Number"
                      {...getFieldProps("vinNo")}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Color"
                      {...getFieldProps("color")}
                    />
                  </Grid>
                </Grid>

                <Box sx={{ mt: 4, pt: 2, display: 'flex', gap: 2, justifyContent: 'flex-end', borderTop: (theme) => `1px solid ${theme.palette.divider}` }}>
                  <Button variant="outlined" color="inherit" onClick={onClose} disabled={isSubmitting}>
                    Cancel
                  </Button>
                  <Button 
                    variant="contained" 
                    type="submit" 
                    disabled={isSubmitting}
                    sx={{
                      background: (theme) => `linear-gradient(135deg, var(--app-color) 0%, ${theme.palette.primary.dark} 100%)`,
                      boxShadow: (theme) => `0 8px 16px 0 ${alpha(theme.palette.primary.main, 0.24)}`,
                      minWidth: 120
                    }}
                  >
                    {isSubmitting ? "Saving..." : isEdit ? "Update Vehicle" : "Add Vehicle"}
                  </Button>
                </Box>
              </Form>
            </FormikProvider>
          </Box>
        )}
      </Box>
    </Modal>
  );
};

export default AddVehicleModal;
